import React, { useEffect, useMemo } from "react";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import BootstrapTable from "react-bootstrap-table-next";
import paginationFactory, { PaginationProvider }  from "react-bootstrap-table2-paginator";
import { getAuthorities } from "../../../../../../_helpers/Helpers";
import * as actions from "../../../_redux/unregistered-students/UnregisteredStudentsAction";
import * as uiHelpers from "../UnregisteredStudentsUIHelpers";
import { 
  getSelectRow, 
  getHandlerTableChange,  
  NoRecordsFoundMessage, 
  PleaseWaitMessage, 
  sortCaret
} from "../../../../../../_metronic/_helpers";
import * as columnFormatters from "./column-formatters";
import { Pagination } from "../../../../../../_metronic/_partials/controls";
import { useUnregisteredStudentsUIContext } from "../UnregisteredStudentsUIContext";

export function UnregisteredStudentsTable({
  deleteUnregisteredStudents
}) {
  // Unregistered Students UI Context
  const unregisteredStudentsUIContext = useUnregisteredStudentsUIContext();
  const unregisteredStudentsUIProps = useMemo(() => {
    return {
      ids: unregisteredStudentsUIContext.ids,
      setIds: unregisteredStudentsUIContext.setIds,
      queryParams: unregisteredStudentsUIContext.queryParams,
      setQueryParams: unregisteredStudentsUIContext.setQueryParams,
      openEditStudentPage: unregisteredStudentsUIContext.openEditStudentPage,
      openFinalRegistrationPage: unregisteredStudentsUIContext.openFinalRegistrationPage
    };
  }, [unregisteredStudentsUIContext]);

  // Getting curret state of Unregistered Students list from store (Redux)
  const { currentState } = useSelector(
    (state) => ({ currentState: state.unregisteredStudents }),
    shallowEqual
  );
  const { totalCount, entities, authorities, listLoading } = currentState;
  
  // Authority Parameters
  const { isEditionAuthorized, isDeletionAuthorized } = getAuthorities(authorities);  
  
  // Unregistered Students Redux state
  const dispatch = useDispatch();

  useEffect(() => {
    unregisteredStudentsUIProps.setIds([]);
    dispatch(actions.getUnregisteredStudents(unregisteredStudentsUIProps.queryParams));
    return () => dispatch(actions.cleanUnregisteredStudentsState());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [unregisteredStudentsUIProps.queryParams, dispatch]);

  // Table columns
  const columns = [
    {
      dataField: "tcKimlikNo",
      text: "TC Kimlik No",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "name",
      text: "Adı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "135px"
      }
    },    
    {
      dataField: "surname",
      text: "Soyadı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "135px"
      }
    },
    {
      dataField: "grade",
      text: "Sınıfı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "120px"
      }
    },
    {
      dataField: "school",
      text: "Okulu",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "200px"
      }
    },   
    {
      dataField: "parentName",
      text: "Veli Adı Soyadı",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "200px"
      }
    },
    {
      dataField: "parentPhone",
      text: "Veli Telefonu",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "150px"
      }
    },
    {
      dataField: "explanation",
      text: "Açıklama",
      sort: true,
      sortCaret: sortCaret,
      style: {
        minWidth: "250px"
      }
    },
    {
      dataField: "action",
      text: "İşlemler",
      formatter: columnFormatters.ActionsColumnFormatter,
      formatExtraData: {
        isEditionAuthorized,
        isDeletionAuthorized,
        openEditStudentPage: unregisteredStudentsUIProps.openEditStudentPage,
        openFinalRegistrationPage: unregisteredStudentsUIProps.openFinalRegistrationPage,
        deleteUnregisteredStudents
      },
      classes: "text-right pr-0",
      headerClasses: "text-right pr-10",
      style: {
        minWidth: "180px"
      }
    }
  ];

  // Table pagination properties
  const paginationOptions = {
    custom: true,
    totalSize: totalCount,
    sizePerPageList: uiHelpers.sizePerPageList,
    sizePerPage: unregisteredStudentsUIProps.queryParams.pageSize,
    page: unregisteredStudentsUIProps.queryParams.pageNumber
  };
  
  return (
    <PaginationProvider pagination={paginationFactory(paginationOptions)}>
      {({ paginationProps, paginationTableProps }) => {
        return (
          <Pagination
            isLoading={listLoading}
            paginationProps={paginationProps}
          >
            <BootstrapTable
              wrapperClasses="table-responsive"
              classes="table table-head-custom table-vertical-center overflow-hidden"
              bootstrap4
              bordered={false}
              remote
              keyField="id"
              data={entities ?? [] }
              columns={columns}
              defaultSorted={uiHelpers.defaultSorted}
              onTableChange={getHandlerTableChange(
                unregisteredStudentsUIProps.setQueryParams
              )}
              selectRow={getSelectRow({
                entities,
                ids: unregisteredStudentsUIProps.ids,
                setIds: unregisteredStudentsUIProps.setIds,
                hidden: !isDeletionAuthorized
              })}
              {...paginationTableProps}
            >
              <PleaseWaitMessage entities={entities} />
              <NoRecordsFoundMessage entities={entities} />
            </BootstrapTable>
          </Pagination>
        );    
      }}
    </PaginationProvider>  
  );
}